import { useMemo, useState } from "react";
import {
  FOGBUGZ_TO_ADO_SLOT_LABELS,
  FOGBUGZ_TO_ADO_SLOT_VALUE_MAX,
  type FogbugzBugBarResult,
  type FogbugzToAdoBugSlotId,
  type FogbugzToAdoPmAddition,
  type FogbugzToAdoSlotVerdict,
} from "@ttc-spectree/toolbox-contract";
import { Chip } from "@/components/grooming/grooming-chips";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Notice, Waiting } from "./parts";

/**
 * The bar step of FogBugz to ADO (epic PI-SPEC-SW-E311, task
 * PI-SPEC-SW-1640-2, Resolved Decision 7b). One row per bug slot: whether it
 * was found in the case, its value and where it was found.
 *
 * - A case with `intent.md` (`source: "intent"`): the slots are read from
 *   it, read-only, with the Bug bar SpecTree checked. No Claude run.
 * - A legacy case (`source: "claude"`): Claude's verdicts. The PM may mark a
 *   found slot "Not in the case" or correct its value (a correction counts
 *   as a PM addition, so the FogBugz note says exactly what the PM added),
 *   then Confirm; only then is the bar checked.
 */

/** How the PM left one found slot. */
type SlotEdit = { mode: "kept" } | { mode: "missing" } | { mode: "edited"; text: string };

/**
 * The confirmed slots and PM additions from Claude's verdicts and the PM's
 * edits: a kept slot as Claude found it; "Not in the case" missing; a
 * corrected value missing in the case with the correction as a PM addition
 * (an unchanged or blank correction keeps the slot as found). Pure; exported
 * for tests.
 */
export function confirmSlots(
  verdicts: readonly FogbugzToAdoSlotVerdict[],
  edits: Partial<Record<FogbugzToAdoBugSlotId, SlotEdit>>,
): { slots: FogbugzToAdoSlotVerdict[]; pmAdditions: FogbugzToAdoPmAddition[] } {
  const slots: FogbugzToAdoSlotVerdict[] = [];
  const pmAdditions: FogbugzToAdoPmAddition[] = [];
  for (const verdict of verdicts) {
    const edit = edits[verdict.slot];
    if (verdict.status !== "found" || !edit || edit.mode === "kept") {
      slots.push(verdict);
      continue;
    }
    if (edit.mode === "missing") {
      slots.push({ slot: verdict.slot, status: "missing" });
      continue;
    }
    const text = edit.text.trim().slice(0, FOGBUGZ_TO_ADO_SLOT_VALUE_MAX).trim();
    if (text === "" || text === (verdict.value ?? "").trim()) {
      slots.push(verdict);
      continue;
    }
    slots.push({ slot: verdict.slot, status: "missing" });
    pmAdditions.push({ slot: verdict.slot, text });
  }
  return { slots, pmAdditions };
}

/** The bar's verdict in one plain line, with the missing rules listed. */
export function BarResult({ bar }: { bar: FogbugzBugBarResult }) {
  if (bar.passes) {
    return (
      <Notice tone="ok" testId="fogbugz-to-ado-bar-pass">
        <p>This case meets the Bug bar: every bug detail is there, and the steps, expected and actual results and environment meet the ADO grooming bar.</p>
      </Notice>
    );
  }
  return (
    <Notice tone="warn" testId="fogbugz-to-ado-bar-missing">
      <p>This case does not meet the Bug bar yet. Still needed:</p>
      <ul className="flex list-disc flex-col gap-1 pl-5">
        {bar.missing.map((miss) => (
          <li key={`${miss.source}:${miss.ruleId}`}>
            <span className="font-medium">{miss.label}</span>: {miss.message}
          </li>
        ))}
      </ul>
    </Notice>
  );
}

export interface BarStepProps {
  caseId: number;
  /** Where the verdicts came from: the case's `intent.md`, or Claude reading a legacy case. */
  source: "intent" | "claude";
  verdicts: readonly FogbugzToAdoSlotVerdict[];
  /** The bar for an `intent.md` case (null while SpecTree checks it). Unused for a legacy case. */
  bar: FogbugzBugBarResult | null;
  /** True while SpecTree checks the bar. */
  checking: boolean;
  /** The bar check failed: one plain sentence. */
  checkError: string | null;
  /** Legacy case: the PM confirmed; check the bar on these. */
  onConfirm: (confirmed: { slots: FogbugzToAdoSlotVerdict[]; pmAdditions: FogbugzToAdoPmAddition[] }) => void;
  /** `intent.md` case: go on with the bar as shown. */
  onContinue: () => void;
}

export function BarStep({ caseId, source, verdicts, bar, checking, checkError, onConfirm, onContinue }: BarStepProps) {
  const [edits, setEdits] = useState<Partial<Record<FogbugzToAdoBugSlotId, SlotEdit>>>({});
  const editable = source === "claude";
  const confirmed = useMemo(() => confirmSlots(verdicts, edits), [verdicts, edits]);
  const setEdit = (slot: FogbugzToAdoBugSlotId, edit: SlotEdit) => {
    setEdits((current) => ({ ...current, [slot]: edit }));
  };

  return (
    <section className="flex flex-col gap-3" aria-labelledby="fogbugz-to-ado-bar" data-testid="fogbugz-to-ado-bar-step">
      <div className="flex flex-col gap-1">
        <h2 id="fogbugz-to-ado-bar" className="font-display text-base font-semibold">
          What case #{caseId} says
        </h2>
        <p className="text-[13px] text-muted-foreground">
          {editable
            ? "Your Claude read the case with the product code open. A detail counts only when it is written in the case; a guess is marked missing. Check each one, correct it if needed, then confirm."
            : "Read from the case's intent.md, written with the FogBugz Formulator."}
        </p>
      </div>

      <ul className="glass-card flex flex-col divide-y divide-[#18181B] rounded-[10px]" data-testid="fogbugz-to-ado-slots">
        {verdicts.map((verdict) => {
          const slot = verdict.slot;
          const edit = edits[slot];
          const found = verdict.status === "found";
          const shownMissing = !found || edit?.mode === "missing";
          const edited = edit?.mode === "edited";
          // "kept" is what Undo leaves: the slot is as Claude found it, so it can be changed again.
          const untouched = edit === undefined || edit.mode === "kept";
          return (
            <li key={slot} className="flex flex-col gap-1.5 px-4 py-3" data-testid={`fogbugz-to-ado-slot-${slot}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">{FOGBUGZ_TO_ADO_SLOT_LABELS[slot]}</span>
                <Chip tone={shownMissing ? "warn" : edited ? "cur" : "ok"}>
                  {shownMissing ? "Missing" : edited ? "Corrected by you" : "Found"}
                </Chip>
                <span className="flex-1" />
                {editable && found && untouched && (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEdit(slot, { mode: "edited", text: verdict.value ?? "" });
                      }}
                    >
                      Correct
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEdit(slot, { mode: "missing" });
                      }}
                    >
                      Not in the case
                    </Button>
                  </>
                )}
                {editable && found && !untouched && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEdit(slot, { mode: "kept" });
                    }}
                  >
                    Undo
                  </Button>
                )}
              </div>
              {found && edit?.mode !== "missing" && !edited && (
                <p className="whitespace-pre-wrap text-[13px] leading-relaxed">{verdict.value}</p>
              )}
              {edited && (
                <textarea
                  aria-label={`${FOGBUGZ_TO_ADO_SLOT_LABELS[slot]} (your correction)`}
                  value={edit.text}
                  maxLength={FOGBUGZ_TO_ADO_SLOT_VALUE_MAX}
                  onChange={(event) => {
                    setEdit(slot, { mode: "edited", text: event.target.value });
                  }}
                  className={cn(
                    "min-h-[72px] w-full rounded-md border border-[#3F3F46] bg-[#111113] px-3 py-2 text-[13px] leading-relaxed outline-none",
                    "focus:border-[#66A1FF] focus:ring-2 focus:ring-[#66A1FF]/25",
                  )}
                />
              )}
              {found && verdict.evidence && edit?.mode !== "missing" && (
                <p className="text-xs text-muted-foreground">Found in: {verdict.evidence}</p>
              )}
            </li>
          );
        })}
      </ul>

      {checkError && (
        <Notice tone="error" testId="fogbugz-to-ado-bar-error">
          <p>{checkError}</p>
        </Notice>
      )}

      {source === "intent" && checking && <Waiting label="Checking the Bug bar" />}
      {source === "intent" && bar && <BarResult bar={bar} />}

      <div className="flex items-center gap-2">
        {editable ? (
          <Button
            type="button"
            disabled={checking}
            onClick={() => {
              onConfirm(confirmed);
            }}
            data-testid="fogbugz-to-ado-confirm-slots"
          >
            {checking ? "Checking the Bug bar…" : "Confirm and check the Bug bar"}
          </Button>
        ) : (
          bar && (
            <Button type="button" onClick={onContinue} data-testid="fogbugz-to-ado-continue">
              {bar.passes ? "Draft the Bug" : "See what to do"}
            </Button>
          )
        )}
      </div>
    </section>
  );
}
